import { isHtml } from './emailParser'

export interface SplitQuoteResult {
  cleanContent: string
  quotedContent: string | null
  hasQuotedText: boolean
  isHtml: boolean
}

/**
 * Split an email body (plain text or HTML) into clean author content and quoted historical thread.
 */
export function splitEmailQuotes(rawContent: string | undefined | null): SplitQuoteResult {
  if (!rawContent || typeof rawContent !== 'string') {
    return {
      cleanContent: '',
      quotedContent: null,
      hasQuotedText: false,
      isHtml: false,
    }
  }

  const trimmed = rawContent.trim()
  if (!trimmed) {
    return {
      cleanContent: '',
      quotedContent: null,
      hasQuotedText: false,
      isHtml: false,
    }
  }

  const isHtmlContent = isHtml(rawContent)

  if (isHtmlContent) {
    return splitHtmlQuotes(rawContent)
  }

  return splitPlainTextQuotes(rawContent)
}

/**
 * Split HTML email body on standard quote containers and headers.
 */
function splitHtmlQuotes(html: string): SplitQuoteResult {
  // Regex for standard HTML quote wrappers
  const htmlQuotePatterns = [
    // Gmail blockquote / div
    /<blockquote[^>]*class=["'][^"']*gmail_quote[^"']*["'][^>]*>[\s\S]*$/i,
    /<div[^>]*class=["'][^"']*gmail_quote[^"']*["'][^>]*>[\s\S]*$/i,
    // Outlook wrappers
    /<div[^>]*id=["']divRplyFwdMsg["'][^>]*>[\s\S]*$/i,
    /<div[^>]*id=["']appendonsend["'][^>]*>[\s\S]*$/i,
    /<div[^>]*class=["'][^"']*OutlookQuote[^"']*["'][^>]*>[\s\S]*$/i,
    // Yahoo / Proton / Moz
    /<div[^>]*class=["'][^"']*yahoo_quoted[^"']*["'][^>]*>[\s\S]*$/i,
    /<div[^>]*class=["'][^"']*protonmail_quote[^"']*["'][^>]*>[\s\S]*$/i,
    /<div[^>]*class=["'][^"']*moz-cite-prefix[^"']*["'][^>]*>[\s\S]*$/i,
    // Forward / Reply horizontal rule separator
    /<hr[^>]*id=["'](?:stopSpelling|null)["'][^>]*>[\s\S]*$/i,
    // Generic blockquote if preceded by text
    /<blockquote[^>]*>[\s\S]*<\/blockquote>\s*$/i,
    // Spanish / English forward/reply headers inside HTML tags
    /<div[^>]*>\s*(?:-{3,}\s*(?:Original Message|Mensaje original|Forwarded message|Mensaje reenviado)\s*-{3,}|(?:On|El)\s+.+?(?:wrote|escribi[oó]):|(?:De|From):\s*.+?)[\s\S]*$/i,
  ]

  for (const pattern of htmlQuotePatterns) {
    const match = pattern.exec(html)
    if (match && match.index > 0) {
      const clean = html.slice(0, match.index).trim()
      const quoted = html.slice(match.index).trim()

      // Strip empty tags or whitespace to ensure clean isn't just an empty wrapper
      const textOnly = clean.replace(/<[^>]+>/g, '').trim()
      if (textOnly.length > 0 && quoted.length > 0) {
        return {
          cleanContent: clean,
          quotedContent: quoted,
          hasQuotedText: true,
          isHtml: true,
        }
      }
    }
  }

  return {
    cleanContent: html,
    quotedContent: null,
    hasQuotedText: false,
    isHtml: true,
  }
}

/**
 * Split Plain Text email body on standard reply/forward headers and quote marks (>).
 */
function splitPlainTextQuotes(text: string): SplitQuoteResult {
  const lines = text.split('\n')
  let quoteStartIndex = -1

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()

    // 1. "On <date> ... wrote:" or "El <date> ... escribió:"
    if (/^(?:On\s+.+?wrote:|El\s+.+?(?:a\s+las\s+.+?)?escribi[oó]:)\s*$/i.test(line)) {
      quoteStartIndex = i
      break
    }

    // 2. Forwarded message / Original message dividers
    if (/^-{3,}\s*(?:Original Message|Mensaje original|Forwarded message|Mensaje reenviado)\s*-{3,}/i.test(line)) {
      quoteStartIndex = i
      break
    }

    // 3. Outlook underline divider
    if (/^_{10,}/.test(line)) {
      quoteStartIndex = i
      break
    }

    // 4. "De: ... \n Enviado/Para/Fecha: ..." or "From: ... \n Sent/To/Date: ..."
    if (/^(?:De|From):\s*.+/i.test(line)) {
      const nextLine1 = (lines[i + 1] || '').trim()
      const nextLine2 = (lines[i + 2] || '').trim()
      const isHeaderBlock =
        /^(?:Enviado el|Sent|Date|Fecha|Para|To|Asunto|Subject):/i.test(nextLine1) ||
        /^(?:Enviado el|Sent|Date|Fecha|Para|To|Asunto|Subject):/i.test(nextLine2)

      if (isHeaderBlock) {
        quoteStartIndex = i
        break
      }
    }

    // 5. Block of lines starting with '>' (at least 2 consecutive '>' lines or 1 '>' line if followed by EOF or more quotes)
    if (/^>\s*/.test(line)) {
      // Check if subsequent non-empty line also has '>'
      let consecutive = 1
      for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
        if (/^>\s*/.test(lines[j].trim())) consecutive++
      }
      if (consecutive >= 1 && i > 0) {
        quoteStartIndex = i
        break
      }
    }
  }

  if (quoteStartIndex > 0) {
    const cleanLines = lines.slice(0, quoteStartIndex)
    const quotedLines = lines.slice(quoteStartIndex)

    const clean = cleanLines.join('\n').trim()
    const quoted = quotedLines.join('\n').trim()

    if (clean.length > 0 && quoted.length > 0) {
      return {
        cleanContent: clean,
        quotedContent: quoted,
        hasQuotedText: true,
        isHtml: false,
      }
    }
  }

  return {
    cleanContent: text,
    quotedContent: null,
    hasQuotedText: false,
    isHtml: false,
  }
}
